import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { any, func } from "prop-types";
import { connect } from "react-redux";
import FormCreator from "../../common/FormCreator";
import {
  formCreatorConvertToValues,
  useForm,
} from "../../common/FormCreator/helpers";
import HorizontalLine from "../../common/HorizontalLine";
import history from "../../history";
import { postDnataForm } from "../../redux/actions/dnataActions";

const EmploymentForm = ({ appData, postDnataFormFunc }) => {
  let initialData = {};
  let initialWorkHistory = [
    {
      from: "",
      to: "",
      company: "",
      referee: "",
      address: "",
      telephone: "",
      email: "",
      reason: "",
      type: "",
    },
  ];
  const initialErrorMessages = [
    {
      toAfterFromDate: "",
      tooBigGap: "",
    },
  ];
  if (appData.dnataOnboarding && appData.dnataOnboarding.employment) {
    initialData = {
      ...initialData,
      ...appData.dnataOnboarding.employment,
    };
  }
  if (
    appData.dnataOnboarding &&
    appData.dnataOnboarding.employment &&
    appData.dnataOnboarding.employment.workHistoryData
  ) {
    initialWorkHistory = Object.keys(
      appData.dnataOnboarding.employment.workHistoryData
    ).map((key) => appData.dnataOnboarding.employment.workHistoryData[key]);
  }

  const isEmpty = (val) => typeof val === "undefined" || val === null;
  const getField = (data) => (fieldName) =>
    !isEmpty(data[fieldName]) ? data[fieldName] : "";

  const getVal = getField(initialData);
  const [shouldDisable, setShouldDisable] = useState(true);
  const [workHistoryData, setWorkHistoryData] = useState(initialWorkHistory);
  const [validationErrorMessage, setValidationErrorMessage] =
    useState(initialErrorMessages);
  const [fiveYearsErrorMessage, setFiveYearsErrorMessage] = useState("");

  useEffect(() => {
    let areAllFieldsFilled = true;
    let areErrorMessagesEmpty = true;

    const validationMessages = workHistoryData.map(() => ({}));
    // check if there are no empty values
    workHistoryData.forEach((el) => {
      if (el.type === "ucb" || el.type === "uncb") {
        Object.keys(el).forEach((key) => {
          if (key !== "company" && key !== "address" && key !== "reason") {
            if (!el[key]) {
              areAllFieldsFilled = false;
            }
          }
        });
      } else {
        Object.keys(el).forEach((key) => {
          if (!el[key]) {
            areAllFieldsFilled = false;
          }
        });
      }
    });

    if (workHistoryData[0].to && workHistoryData[0].from) {
      let oldestDate = moment(workHistoryData[0].to).format("YYYY-MM-DD");
      let earliestDate = moment(workHistoryData[0].from).format("YYYY-MM-DD");

      workHistoryData.forEach((el, i) => {
        const from = el.from ? moment(el.from).format("YYYY-MM-DD") : null;
        const to = el.to ? moment(el.to).format("YYYY-MM-DD") : null;

        // Validation if 'from' date is before 'to' date validation
        if (from && to && moment(to).isBefore(from)) {
          const prev = validationMessages[i] ? validationMessages[i] : {};
          validationMessages[i] = {
            ...prev,
            toAfterFromDate: "From date can not be after to date",
          };
        }

        if (from && moment(from).isBefore(oldestDate)) {
          oldestDate = from;
        }

        if (to && moment(to).isAfter(earliestDate)) {
          earliestDate = to;
        }
      });

      // Validation whole time (min 5 years /(- 28 days))
      if (oldestDate && earliestDate) {
        const r = moment(earliestDate).diff(moment(oldestDate), "years", true);

        if (!(r >= 4.924731182795699)) {
          setFiveYearsErrorMessage(
            "Please ensure the work history you have entered covers the last 5 years."
          );
        } else {
          setFiveYearsErrorMessage("");
        }
      }

      // Validation difference in time between jobs (max 28 days)
      for (let i = 0; workHistoryData.length - 1; i += 1) {
        if (i === workHistoryData.length - 1) {
          break;
        }
        if (
          workHistoryData[i + 1].from &&
          workHistoryData[i + 1].to &&
          workHistoryData[i].from &&
          workHistoryData[i].to
        ) {
          const start = moment(workHistoryData[i + 1].to, "YYYY/MM/DD");
          const end = moment(workHistoryData[i].from, "YYYY/MM/DD");
          const r = moment(end).diff(moment(start), "days", true);
          if (r > 28) {
            const prev = validationMessages[i + 1]
              ? validationMessages[i + 1]
              : {};
            validationMessages[i + 1] = {
              ...prev,
              tooBigGap:
                "You have a gap of more than 28 days within your work history, please amend as we can not accept a gap of more than 28 days without details of a character reference to contact.",
            };
          }
        }
      }
    }

    validationMessages.forEach((el) => {
      Object.keys(el).forEach((key) => {
        if (el[key] && Object.keys(el[key]).length > 0) {
          areErrorMessagesEmpty = false;
        }
      });
    });

    setValidationErrorMessage(validationMessages);

    if (
      areAllFieldsFilled &&
      areErrorMessagesEmpty &&
      fiveYearsErrorMessage === ""
    ) {
      setShouldDisable(false);
    } else {
      setShouldDisable(true);
    }
  }, [shouldDisable, workHistoryData, fiveYearsErrorMessage]);

  const initialState = {
    blurbSection4: {
      htmlObj: (
        <div>
          <HorizontalLine text={"Section 4 - UK Employment"} />
        </div>
      ),
    },
    Group10: {
      title:
        "Are there any restrictions to your residence in the UK which might affect your right to take up employment in the UK?",
      type: "checkbox",
      value: getVal("Group10"),
      mandatory: true,
    },
    details: {
      title: "If yes, please provide further details:",
      value: getVal("details"),
      type: "textarea",
      mandatory: true,
      dependOn: [{ fieldKey: "Group10" }],
    },
    Group11: {
      title:
        "Which document are you able to provide to determine proof of eligibility to work in the UK should an offer of employment be made to you?",
      type: "radio",
      value: getVal("Group11"),
      radioOptions: [
        "UK Passport",
        "EU Passport",
        "Visa/Residency Permit",
        "National ID Card",
      ],
      mandatory: true,
    },
    Group12: {
      title:
        "If you are successful in your application, would you require a work permit prior to taking up the employment?",
      type: "checkbox",
      value: getVal("Group12"),
      mandatory: true,
    },
    blurbSection4a: {
      htmlObj: (
        <div>
          <HorizontalLine text={"Section 5 - Your Five Year History"} />
          In accordance with the Civil Aviation Authority regulations dnata
          catering UK Ltd are required to establish and verify that all
          potential employees have been seen at least once every 28 days within
          the past five years. On the following pages you will be asked to
          provide comprehensive account of your continous employment and / or
          unemployment, education or travel history - this is for referencing
          purposes only in order for us to be security compliant and it is vital
          that you provide us with complete and accurate information. Each entry
          must include the full name of your previous Company, School,
          University or Gap referee. You must provide a contact name, full
          postal address and contact telephone number and / or email address.
          Periods of unemployment will not affect your possibility of employment
          with dnata catering UK Ltd but must be declared as part of your five
          year history.{" "}
          <b>
            dnata catering UK need to capture any periods of unemployment you
            have had within the last five years that lasted 28 days or longer.
          </b>
          <br />
          <br />
          {`All referees enclosed within this application form will be contacted
          for a reference in order for your whereabouts to be verified; dnata
          catering UK expects that by enclosing their details you are indicating
          that the referee's consent has been permitted.`}
        </div>
      ),
    },
    blurbSection4b: {
      htmlObj: (
        <div style={{ display: "flex", textAlign: "center", color: "#4d91b3" }}>
          <p style={{ fontWeight: "bold", width: "100%" }}>
            Please include all periods of employment, unemployment, education
            and travel over the last 5 years below, starting with the most
            recent. Please note a failure to supply sufficient information will
            result in us being unable to progress your application.
          </p>
          <HorizontalLine />
        </div>
      ),
    },
  };

  const [fields, setField] = useForm(initialState);

  const handleSave = () => {
    const fieldValuesPayload = formCreatorConvertToValues(fields);

    postDnataFormFunc("employment", {
      ...fieldValuesPayload,
      workHistoryData,
    });
    history.push("/profile/edit/dnata-onboarding");
  };

  const setValue = (value, key, i) => {
    const mappedHistoryData = workHistoryData.map((el, index) => {
      if (i === index) {
        return { ...el, [key]: value };
      }
      return el;
    });

    setWorkHistoryData(mappedHistoryData);
  };

  const removeLast = () => {
    const newArr = [...workHistoryData];
    newArr.splice(newArr.length - 1);
    setWorkHistoryData(newArr);
  };

  const inputStyle = { width: "20px", height: "20px", marginRight: "5px" };

  const showAstrix = (el) => {
    if (el.type === "ucb" || el.type === "uncb") {
      return "";
    }
    return "*";
  };

  const customHtml = (
    <Fragment>
      {workHistoryData.map((el, i) => (
        <Fragment key={i}>
          <HorizontalLine text="Work History" desc={`Part ${i + 1}`} />
          <form>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                margin: "1rem 0",
              }}
              onChange={(e) => setValue(e.target.value, "type", i)}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  style={inputStyle}
                  type="radio"
                  value="employed"
                  name="type"
                  checked={el.type === "employed"}
                />{" "}
                Employed
              </div>
              <div
                style={{
                  padding: "5px 0",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  style={inputStyle}
                  type="radio"
                  value="education"
                  name="type"
                  checked={el.type === "education"}
                />{" "}
                Education
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  style={inputStyle}
                  type="radio"
                  value="self-employment"
                  name="type"
                  checked={el.type === "self-employment"}
                />{" "}
                Self-employment
              </div>
              <div
                style={{
                  padding: "5px 0",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  style={inputStyle}
                  type="radio"
                  value="voluntary"
                  name="type"
                  checked={el.type === "voluntary"}
                />{" "}
                Voluntary
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  style={inputStyle}
                  type="radio"
                  value="agency"
                  name="type"
                  checked={el.type === "agency"}
                />{" "}
                Agency
              </div>
              <div
                style={{
                  padding: "5px 0",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  style={inputStyle}
                  type="radio"
                  value="ucb"
                  name="type"
                  checked={el.type === "ucb"}
                />{" "}
                Unemployed claiming benefits
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  style={inputStyle}
                  type="radio"
                  value="uncb"
                  name="type"
                  checked={el.type === "uncb"}
                />{" "}
                Unemployed NOT claiming benefits
              </div>
            </div>
            <label className="grey-text">From*</label>
            <input
              type="date"
              value={el.from}
              onChange={(e) => setValue(e.target.value, "from", i)}
              required
              className="form-control"
            />
            <label className="grey-text">To*</label>
            <input
              value={el.to}
              onChange={(e) => setValue(e.target.value, "to", i)}
              required
              type="date"
              className="form-control"
            />
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage[i] &&
                validationErrorMessage[i].toAfterFromDate}
            </p>
            <p style={{ color: "red", fontSize: "13px" }}>
              {validationErrorMessage[i] && validationErrorMessage[i].tooBigGap}
            </p>
            <label className="grey-text">Company Name{showAstrix(el)}</label>
            <input
              onChange={(e) => setValue(e.target.value, "company", i)}
              value={el.company}
              className="form-control"
            />
            <label className="grey-text">Referee Name*</label>
            <input
              onChange={(e) => setValue(e.target.value, "referee", i)}
              value={el.referee}
              required
              className="form-control"
            />
            <label className="grey-text">Address{showAstrix(el)}</label>
            <input
              onChange={(e) => setValue(e.target.value, "address", i)}
              value={el.address}
              required={el.type !== "ucb" || el.type !== "uncb"}
              className="form-control"
            />
            <label className="grey-text">Telephone*</label>
            <input
              onChange={(e) => setValue(e.target.value, "telephone", i)}
              value={el.telephone}
              required
              className="form-control"
            />
            <label className="grey-text">Email*</label>
            <input
              onChange={(e) => setValue(e.target.value, "email", i)}
              value={el.email}
              required={el.type !== "ucb" || el.type !== "uncb"}
              className="form-control"
            />
            <label className="grey-text">
              Reason for leaving{showAstrix(el)}
            </label>
            <input
              onChange={(e) => setValue(e.target.value, "reason", i)}
              value={el.reason}
              required={el.type !== "ucb" || el.type !== "uncb"}
              className="form-control"
            />
          </form>
        </Fragment>
      ))}
      <div>
        <p style={{ color: "orange", fontSize: "14px" }}>
          {fiveYearsErrorMessage && fiveYearsErrorMessage}
        </p>
        {
          <button
            disabled={workHistoryData.length === 1}
            onClick={removeLast}
            className="btn btn-secondary my-2"
          >
            Remove Last
          </button>
        }
        <button
          onClick={() =>
            setWorkHistoryData([
              ...workHistoryData,
              {
                from: "",
                to: "",
                company: "",
                referee: "",
                address: "",
                telephone: "",
                email: "",
                reason: "",
              },
            ])
          }
          className="btn btn-secondary m-2 "
        >
          {" "}
          Add More{" "}
        </button>
      </div>
    </Fragment>
  );

  return (
    <Fragment>
      <FormCreator
        formTitle={"Employment"}
        fields={fields}
        setField={setField}
        handleCancel={() => history.push("/profile/edit/dnata-onboarding")}
        onboardingBranch={"dnata"}
        customHtml={customHtml}
        handleSave={handleSave}
        disableSave={shouldDisable}
        isCustomSaveButton={true}
      />
    </Fragment>
  );
};
EmploymentForm.propTypes = {
  appData: any,
  postDnataFormFunc: func.isRequired,
};
const mapDispatchToProps = {
  postDnataFormFunc: postDnataForm,
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});
const withRedux = connect(mapStateToProps, mapDispatchToProps);
export default withRedux(EmploymentForm);
